// Extension detection, auth relay, and command protocol
// All postMessage calls use window.location.origin for security
// Single persistent listener routes responses by requestId (#7)

import { AppErrorImpl, ErrorCode, FeatureDeniedError } from './errors'

const ORIGIN = window.location.origin

/**
 * Minimum extension version the web app is willing to talk to. Bump this
 * whenever you ship a web-app change that requires a matching extension change
 * (new command shape, renamed field, etc.). The web app shows a blocking
 * "please update" banner to users running older versions.
 *
 * Must be bumped in lockstep with extension/manifest.json's `version`.
 */
export const MIN_EXTENSION_VERSION = '1.0.4'

/**
 * Latest extension version you've published. When a user's installed version
 * is >= MIN but < LATEST, the web app shows a soft "update available" nudge
 * — useful for non-breaking improvements (bug fixes, perf, new features that
 * also gracefully fall back).
 *
 * Leave equal to MIN when there's nothing new to nudge about.
 */
export const LATEST_EXTENSION_VERSION = '1.0.4'

// Persistent response router — one listener for all commands
const pendingCommands = new Map<string, {
  resolve: (data: unknown) => void
  reject: (err: Error) => void
  timeout: ReturnType<typeof setTimeout>
}>()

function errorMessage(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (typeof record.message === 'string') return record.message
    if (typeof record.error === 'string') return record.error
    try {
      return JSON.stringify(value)
    } catch {
      return 'Command failed'
    }
  }
  return value == null ? 'Command failed' : String(value)
}

// Install once
if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    // Reject messages from iframes / other windows. Without this guard an
    // embedded iframe (ad, widget, tracker) on fwdslash.dev could post a
    // spoofed FWDSLASH_RESPONSE and resolve arbitrary pending commands with
    // attacker-controlled data. The origin check alone isn't enough because
    // a same-origin iframe passes it.
    if (event.source !== window) return
    if (event.origin !== ORIGIN) return
    if (event.data?.type === 'FWDSLASH_RESPONSE') {
      const { requestId, ok, data, error } = event.data
      const pending = pendingCommands.get(requestId)
      if (!pending) return
      pendingCommands.delete(requestId)
      clearTimeout(pending.timeout)
      if (ok) {
        pending.resolve(data)
      } else {
        pending.reject(new Error(errorMessage(error)))
      }
    }
  })
}

export function isExtensionInstalled(): boolean {
  return !!document.getElementById('fwdslash-ext')
}

export function getExtensionVersion(): string | null {
  const el = document.getElementById('fwdslash-ext')
  return el?.dataset.version ?? null
}

/**
 * Per-page-load nonce the extension stamps on the sentinel. We echo it on
 * every outbound FWDSLASH_* message so the content script can reject
 * messages from other sources (notably a reflected-XSS on this origin
 * that doesn't know to look for the nonce). Returns `null` when the
 * extension isn't installed — in that case callers skip posting at all.
 *
 * Read fresh on each call (the DOM is the source of truth) so a late-
 * injected sentinel becomes usable without a React re-render.
 */
function getExtensionNonce(): string | null {
  const el = document.getElementById('fwdslash-ext') as HTMLElement | null
  return el?.dataset.nonce ?? null
}

/**
 * Compare two dotted-numeric version strings. Returns -1 / 0 / 1.
 * Non-numeric or missing segments sort as 0. Intentionally simple — good
 * enough for `MAJOR.MINOR.PATCH` without pre-release suffixes.
 */
function compareVersions(a: string, b: string): number {
  const as = a.split('.').map((n) => parseInt(n, 10) || 0)
  const bs = b.split('.').map((n) => parseInt(n, 10) || 0)
  const len = Math.max(as.length, bs.length)
  for (let i = 0; i < len; i++) {
    const av = as[i] ?? 0
    const bv = bs[i] ?? 0
    if (av > bv) return 1
    if (av < bv) return -1
  }
  return 0
}

/**
 * True if the installed extension version satisfies MIN_EXTENSION_VERSION.
 * Returns true when no version is detectable (no extension installed) — the
 * "not installed" banner handles that case separately.
 */
export function isExtensionVersionSupported(version: string | null): boolean {
  if (!version) return true
  return compareVersions(version, MIN_EXTENSION_VERSION) >= 0
}

/**
 * True if a newer non-breaking extension version is available. Returns false
 * when the installed version is at LATEST or when unsupported (the latter is
 * covered by isExtensionVersionSupported's blocker banner, not this nudge).
 */
export function isExtensionUpdateAvailable(version: string | null): boolean {
  if (!version) return false
  if (!isExtensionVersionSupported(version)) return false
  return compareVersions(version, LATEST_EXTENSION_VERSION) < 0
}

/**
 * Throw `FeatureDeniedError` (ADR 0003) if the installed extension version
 * is below `minVersion`, or the extension isn't installed at all.
 *
 * Use inside a hook or command-caller that depends on a specific extension
 * capability introduced in that version. The thrown error carries a
 * user-facing message so the call site can `catch` and surface it via
 * toast / banner — or let it bubble through the mutation registry (ADR
 * 0013 when it lands) for uniform handling.
 *
 *   // Inside a hook that calls a new GET_VIEWER_CAPABILITIES command
 *   // that only exists in extension >= 1.1.0:
 *   requireExtensionVersion('1.1.0', 'Sales Navigator integration')
 *
 * Version comparison uses the same dotted-numeric `compareVersions`
 * helper as `isExtensionVersionSupported`, so the blocker banner and
 * this check agree.
 */
export function requireExtensionVersion(minVersion: string, featureName: string): void {
  const installed = getExtensionVersion()
  if (installed === null) {
    throw new FeatureDeniedError(
      `${featureName} requires the Slash extension to be installed.`,
    )
  }
  if (compareVersions(installed, minVersion) < 0) {
    throw new FeatureDeniedError(
      `${featureName} requires Slash extension v${minVersion} or later. ` +
      `You have v${installed}. Open chrome://extensions and click Update.`,
    )
  }
}

/**
 * Narrow union of every message shape the web app posts to inject-id.js.
 * Keeping the shapes explicit means adding a new command forces a
 * compile-time update here, preventing drift between the web-side sender
 * and the extension-side handler.
 */
type OutboundMessage =
  | { type: 'FWDSLASH_SESSION'; id: string; userId: string }
  | { type: 'FWDSLASH_ENRICHMENT_CAPABILITY'; capability: ExtensionEnrichmentCapabilityMessage | null }
  | { type: 'FWDSLASH_SIGNOUT' }
  | { type: 'FWDSLASH_ACCOUNT_DELETED' }
  | { type: 'FWDSLASH_COMMAND'; command: string; payload: Record<string, unknown>; requestId: string }
  | { type: 'FWDSLASH_COMMAND_ABORT'; requestId: string }

export interface ExtensionEnrichmentCapabilityMessage {
  token: string
  expiresAt: number
  teamId: string
  provider: string
  functionsBaseUrl: string
  functionsAnonKey: string
}

/**
 * Shared helper for every outbound postMessage to the inject-id content
 * script. Single seam means: one origin to audit, one call site to
 * instrument when we add telemetry, and no accidental postMessage(..., '*')
 * regressions slipping past code review. Also stamps the per-page nonce
 * the extension expects — without that the content script drops the
 * message on the floor.
 *
 * Returns `false` when the extension isn't installed (no sentinel → no
 * nonce). Callers that need to distinguish "no extension" from "extension
 * rejected the message" can check the return value; the existing command
 * paths all fall through to their own timeout/ack handling regardless.
 */
function postToContent(message: OutboundMessage): boolean {
  const nonce = getExtensionNonce()
  if (!nonce) return false
  window.postMessage({ ...message, nonce }, ORIGIN)
  return true
}

/**
 * TODO(protocol-unify): relaySession / relaySignOut / relayAccountDeleted
 * predate sendCommand and roll their own request/ack correlation. The goal
 * is to route them under sendCommand once the service worker's SESSION
 * handling stops needing to run before engine init (tracked with the Phase
 * 5 auth rework). Until then they share the same postMessage transport via
 * `postToContent` above; the wire format divergence is deliberate.
 *
 * Cancellation (POST_FIX_ASTAR_AUDIT_HANDOFF finding #3): the caller may
 * pass an `AbortSignal`. Pre-aborted signals short-circuit BEFORE the
 * postMessage — i.e. the extension never receives the (stale) session
 * payload. Mid-flight aborts (after postMessage) stop waiting for ACK
 * but can't recall the message already in flight; callers that care
 * about that window should post a follow-up `relaySignOut()` which the
 * extension processes in-order.
 *
 * Resolves `true` when the extension acks success within 5s, `false` on
 * NAK / timeout / abort / no-extension.
 */
export function relaySession(
  _accessToken: string,
  _refreshToken: string,
  userId: string,
  signal?: AbortSignal,
): Promise<boolean> {
  return new Promise((resolve) => {
    // Pre-abort short-circuit: never notify the extension at all. The
    // extension authenticates to LinkedIn with browser cookies; Supabase
    // access/refresh tokens intentionally stay in the web app.
    if (signal?.aborted) {
      resolve(false)
      return
    }

    // Correlation ID: without this, two concurrent relaySession calls
    // (e.g. rapid token refresh on Supabase session rotation) both listen
    // for the next FWDSLASH_SESSION_ACK and the first ack fulfils
    // whichever listener won the race — the other resolves too, possibly
    // with the wrong ok value. Threading an id through the request and
    // requiring the ack to echo it guarantees each caller observes its
    // own result. inject-id.js is updated to echo data.id.
    const id = crypto.randomUUID()
    let settled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let abortListener: (() => void) | null = null

    const cleanup = () => {
      window.removeEventListener('message', handler)
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
      if (abortListener && signal) {
        signal.removeEventListener('abort', abortListener)
        abortListener = null
      }
    }
    const settle = (ok: boolean) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(ok)
    }

    const handler = (event: MessageEvent) => {
      // Same same-window guard as the FWDSLASH_RESPONSE listener above.
      if (event.source !== window) return
      if (event.origin !== ORIGIN) return
      if (event.data?.type !== 'FWDSLASH_SESSION_ACK') return
      if (event.data?.id !== id) return
      settle(!!event.data.ok)
    }
    window.addEventListener('message', handler)

    if (signal) {
      abortListener = () => settle(false)
      signal.addEventListener('abort', abortListener, { once: true })
    }

    postToContent({
      type: 'FWDSLASH_SESSION',
      id,
      userId,
    })

    // Re-check after attaching the listener + posting. If `signal.aborted`
    // flipped between the top-of-executor check and now (e.g. a
    // microtask fired abort synchronously), the listener has already
    // dispatched its one-shot fire and won't re-fire. Settle manually
    // so we don't hang for the full 5s.
    if (signal?.aborted) {
      settle(false)
      return
    }

    timer = setTimeout(() => settle(false), 5000)
  })
}

export function relaySignOut() {
  postToContent({ type: 'FWDSLASH_SIGNOUT' })
}

export function relayEnrichmentCapability(capability: ExtensionEnrichmentCapabilityMessage | null): boolean {
  return postToContent({ type: 'FWDSLASH_ENRICHMENT_CAPABILITY', capability })
}

export function relayAccountDeleted() {
  postToContent({ type: 'FWDSLASH_ACCOUNT_DELETED' })
}

export interface SendCommandOptions {
  /**
   * Abort the pending command. Resolves the promise with an `AbortError` so
   * TanStack Query, fetch-style consumers, and `toAppError` can all treat
   * cancellation uniformly. Also notifies the extension so it can stop any
   * in-flight LinkedIn fetch (the extension may ignore; the client-side
   * rejection happens either way).
   */
  signal?: AbortSignal
  /** Override the default 30s timeout. Set to `Infinity` to disable. */
  timeoutMs?: number
}

export function sendCommand<T = unknown>(
  command: string,
  payload: Record<string, unknown> = {},
  options: SendCommandOptions = {},
): Promise<T> {
  const { signal, timeoutMs = 30_000 } = options

  return new Promise<T>((resolve, reject) => {
    // Bail synchronously if the caller pre-aborted.
    if (signal?.aborted) {
      reject(new DOMException('Command aborted', 'AbortError'))
      return
    }

    const requestId = crypto.randomUUID()

    const finish = (fn: () => void) => {
      pendingCommands.delete(requestId)
      clearTimeout(timeout)
      if (abortListener && signal) signal.removeEventListener('abort', abortListener)
      fn()
    }

    const timeout = Number.isFinite(timeoutMs)
      ? setTimeout(() => finish(() => reject(new Error(`Command ${command} timed out`))), timeoutMs)
      : (undefined as unknown as ReturnType<typeof setTimeout>)

    let abortListener: (() => void) | null = null
    if (signal) {
      abortListener = () => {
        // Best-effort notification to the extension. It may not handle this yet;
        // client-side cancellation still works either way.
        try {
          postToContent({ type: 'FWDSLASH_COMMAND_ABORT', requestId })
        } catch { /* ignored */ }
        finish(() => reject(new DOMException('Command aborted', 'AbortError')))
      }
      signal.addEventListener('abort', abortListener, { once: true })

      // Re-check after attaching the listener. If `signal.aborted` flipped
      // true between the early guard at the top of this Promise executor
      // and now (a microtask scheduled an abort() synchronously between
      // those points), the listener will never fire because the event has
      // already dispatched. Invoke it manually so the command doesn't hang
      // until timeoutMs.
      if (signal.aborted) {
        abortListener()
        return
      }
    }

    pendingCommands.set(requestId, {
      resolve: (data) => finish(() => resolve(data as T)),
      reject: (err) => finish(() => reject(err)),
      timeout,
    })

    const posted = postToContent({
      type: 'FWDSLASH_COMMAND',
      command,
      payload,
      requestId,
    })

    // Fail fast if there's no extension to post to — otherwise the command
    // would sit in pendingCommands until timeoutMs (30s by default) only
    // to reject with "timed out", masking the real cause.
    //
    // Thrown as an AppError with code `extension/missing` so the telemetry
    // layer can recognise it as control flow (expected on any browser
    // without the extension installed) and drop it before it reaches
    // Sentry. See `telemetry.ts` beforeSend + forwardLogToSentry.
    if (!posted) {
      finish(() => reject(new AppErrorImpl({
        message: 'Extension not installed',
        code: ErrorCode.EXTENSION_MISSING,
      })))
    }
  })
}
